import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CollapsibleCard } from '@/components/common';
import { EmptyState } from '@/components/ui';
import { customerActivityService, type CustomerActivityItem } from '@/api/services/customerActivity';
import { subscribeCustomerActivityRefresh } from '@/utils/customerActivityRefresh';
import { tActivity } from '@/i18n/activity';
import ActivityCard from './ActivityCard';
import ActivityViewAllLink from './ActivityViewAllLink';

type RefreshProps = {
  /** When this value changes, data reloads in the background without clearing the list. */
  refreshSignal?: unknown;
};

type CollapsibleSectionProps = RefreshProps & {
  storageKey: string;
  storageScope?: string;
  defaultExpanded?: boolean;
};

function useAttentionItems(refreshSignal?: unknown) {
  const [items, setItems] = useState<CustomerActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const load = useCallback(async (background = false) => {
    const keepVisible = background && loadedRef.current;
    if (!keepVisible) setLoading(true);
    try {
      const nextItems = await customerActivityService.attention();
      setItems(nextItems);
      loadedRef.current = nextItems.length > 0;
    } catch {
      if (!keepVisible) {
        setItems([]);
        loadedRef.current = false;
      }
    } finally {
      if (!keepVisible) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadedRef.current = false;
    void load(false);
  }, [load]);

  useEffect(() => {
    if (refreshSignal == null) return;
    void load(true);
  }, [load, refreshSignal]);

  useEffect(() => {
    return subscribeCustomerActivityRefresh(() => {
      void load(true);
    });
  }, [load]);

  return { items, loading };
}

function AttentionList({ items }: { items: CustomerActivityItem[] }) {
  return (
    <div className="attention-panel__list">
      {items.map((item) => (
        <ActivityCard key={item.id} item={item} compact />
      ))}
    </div>
  );
}

/** Collapsible dashboard section with empty and loading states. */
export function AttentionCollapsibleSection({
  refreshSignal,
  storageKey,
  storageScope = 'dashboard',
  defaultExpanded = true,
}: CollapsibleSectionProps) {
  const { items, loading } = useAttentionItems(refreshSignal);

  return (
    <CollapsibleCard
      storageKey={storageKey}
      storageScope={storageScope}
      title={tActivity('activity.attention.title')}
      defaultExpanded={defaultExpanded}
      loading={loading}
      actions={<ActivityViewAllLink />}
    >
      {!loading && items.length === 0 ? (
        <EmptyState
          title={tActivity('activity.attention.empty.title')}
          description={tActivity('activity.attention.empty.description')}
        />
      ) : (
        <AttentionList items={items} />
      )}
    </CollapsibleCard>
  );
}

/** Standalone card for non-dashboard pages (e.g. customer activity). */
export default function AttentionPanel({ refreshSignal }: RefreshProps) {
  const { items, loading } = useAttentionItems(refreshSignal);
  if (!loading && items.length === 0) return null;

  return (
    <section className="card section attention-panel" aria-labelledby="attention-panel-title">
      <div className="attention-panel__header">
        <h2 id="attention-panel-title">{tActivity('activity.attention.title')}</h2>
        <ActivityViewAllLink />
      </div>
      {loading ? (
        <div className="loading-inline" role="status">
          <span className="spinner" aria-hidden />
          Loading…
        </div>
      ) : (
        <AttentionList items={items} />
      )}
    </section>
  );
}
